import { useState, useEffect, useRef, useCallback, useMemo, type RefObject, type PointerEvent as ReactPointerEvent } from "react";
import { useFullscreenSwipeDismiss } from "@/hooks/useFullscreenSwipeDismiss";
import {
  useMediaViewerGestureLock,
  useMediaViewerZoomState,
} from "@/lib/native/mediaViewerGestureLock";
import { createPortal } from "react-dom";
import { Heart, Share2, Loader2, Lock, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";

import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

import { NativeImage } from "@/components/native/NativeImage";
import { ZoomableImage } from "@/components/native/ZoomableImage";
import { cn } from "@/lib/utils";
import { useMedia } from "@/hooks/useMedia";
import {
  VehicleTestImages,
  ExteriorImage,
  is360ExteriorFrame,
} from "@/types/vehicleTest";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useNativeMediaViewerOpen } from "@/hooks/useNativeMediaViewerOpen";
import { getShareableAppUrl } from "@/lib/native/openBileniaWeb";
import { mediaViewerZClass } from "@/lib/native/nativeZIndex";
import {
  isGalleryFullscreenOpen,
  setGalleryFullscreenOpen,
} from "@/lib/galleryFullscreenLock";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { isVideoMedia } from "@/lib/isVideoMedia";

type GallerySlide =
  | (ExteriorImage & { category: string })
  | { category: string; isGuestPaywall: true; url: "" };

type GalleryPhoto = ExteriorImage & { category: string };

function isEngineVideoSlide(img: {
  category?: string;
  type?: string;
  url?: string;
}): boolean {
  return (
    img.category === "exterior" && isVideoMedia(img.type, img.url)
  );
}

/** WebView often leaves `preload="metadata"` blank — seek a hair in to paint a frame. */
function EngineVideoThumb({
  src,
  className,
}: {
  src: string;
  className?: string;
}) {
  return (
    <video
      src={src}
      muted
      playsInline
      preload="auto"
      className={className}
      onLoadedMetadata={(e) => {
        const v = e.currentTarget;
        try {
          const dur = Number.isFinite(v.duration) ? v.duration : 0;
          v.currentTime = dur > 0.25 ? 0.12 : Math.min(0.05, Math.max(dur / 4, 0.01));
        } catch {
          /* seek not supported yet */
        }
      }}
    />
  );
}

/** Center play/pause overlays — avoid click on <video> (doubles with native controls). */
function GalleryVideoPlayer({
  src,
  className,
  onReady,
}: {
  src: string;
  className?: string;
  onReady?: () => void;
}) {
  const { language } = useLanguage();
  const labels = carDetailsLabels(language).gallery;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setDecodeFailed(false);
  }, [src]);

  const play = useCallback(() => {
    void videoRef.current?.play().catch(() => undefined);
  }, []);

  const pause = useCallback(() => {
    videoRef.current?.pause();
  }, []);

  return (
    <div className={cn("relative max-w-full max-h-full", className)}>
      <video
        ref={videoRef}
        src={src}
        playsInline
        preload="metadata"
        controls={playing && !decodeFailed}
        className="max-w-full max-h-full w-auto h-auto object-contain bg-black"
        onLoadedData={onReady}
        onError={() => {
          setDecodeFailed(true);
          onReady?.();
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      {decodeFailed ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 px-4 text-center">
          <p className="max-w-xs text-sm text-white/90">
            {labels.engineVideoUnsupported}
          </p>
        </div>
      ) : !playing ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            play();
          }}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/20"
          aria-label="Play"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/55 text-white shadow-lg ring-2 ring-white/35 backdrop-blur-sm transition-transform hover:scale-105 active:scale-95">
            <Play className="h-8 w-8 fill-white translate-x-0.5" strokeWidth={0} />
          </span>
        </button>
      ) : (
        /* Tap center to pause; leave bottom free for native control bar */
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            pause();
          }}
          className="absolute inset-x-0 top-0 bottom-14 z-10 cursor-default"
          aria-label="Pause"
        />
      )}
    </div>
  );
}

function GalleryThumbStrip({
  images,
  currentIndex,
  scrollRef,
  onSelect,
  isPaywall,
  ariaLabelPrefix,
  variant = "default",
  embedded = false,
}: {
  images: GallerySlide[];
  currentIndex: number;
  scrollRef: RefObject<HTMLDivElement | null>;
  onSelect: (index: number) => void;
  isPaywall: (img: GallerySlide) => boolean;
  ariaLabelPrefix: string;
  variant?: "default" | "fullscreen";
  /** When true, omit outer chrome (parent provides the row). */
  embedded?: boolean;
}) {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startScroll: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    setCanScrollPrev(el.scrollLeft > 2);
    setCanScrollNext(el.scrollLeft < max - 2);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    const onScroll = () => updateScrollState();
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;

      // Prefer the dominant axis; map vertical wheel → horizontal strip
      let delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;

      if (e.deltaMode === 1) delta *= 16; // lines → px
      else if (e.deltaMode === 2) delta *= el.clientWidth; // pages → px

      e.preventDefault();
      e.stopPropagation();
      el.scrollLeft += delta;
      updateScrollState();
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: false });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    // Content width can settle after images load
    const t = window.setTimeout(updateScrollState, 50);

    return () => {
      window.clearTimeout(t);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", onWheel);
      ro.disconnect();
    };
  }, [scrollRef, images.length, updateScrollState]);

  const scrollByDir = useCallback(
    (dir: -1 | 1) => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({
        left: dir * Math.max(180, el.clientWidth * 0.7),
        behavior: "smooth",
      });
    },
    [scrollRef],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      if ((e.target as Element).closest("button[data-gallery-dot]")) return;
      const el = scrollRef.current;
      if (!el || el.scrollWidth <= el.clientWidth + 1) return;
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startScroll: el.scrollLeft,
        moved: false,
      };
      el.setPointerCapture(e.pointerId);
    },
    [scrollRef],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      const el = scrollRef.current;
      if (!drag || !el || drag.pointerId !== e.pointerId) return;
      const dx = e.clientX - drag.startX;
      if (!drag.moved && Math.abs(dx) > 4) {
        drag.moved = true;
        suppressClickRef.current = true;
      }
      if (drag.moved) {
        el.scrollLeft = drag.startScroll - dx;
        updateScrollState();
      }
    },
    [scrollRef, updateScrollState],
  );

  const endPointerDrag = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const el = scrollRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (el?.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    if (drag.moved) {
      // Allow next click after this gesture finishes
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
  }, [scrollRef]);

  const handleThumbClick = useCallback(
    (index: number) => {
      if (suppressClickRef.current) return;
      onSelect(index);
    },
    [onSelect],
  );

  const isFullscreen = variant === "fullscreen";

  return (
    <div
      className={cn(
        "relative w-full",
        isFullscreen
          ? "pointer-events-none"
          : embedded
            ? ""
            : "bg-background border-t border-border/50 rounded-b-none sm:rounded-b-lg py-2",
      )}
    >
      <div
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointerDrag}
        onPointerCancel={endPointerDrag}
        className={cn(
          "w-full overflow-x-auto overscroll-x-contain touch-pan-x scroll-px-2",
          "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
          embedded
            ? "px-2 sm:px-3 cursor-grab active:cursor-grabbing select-none"
            : "px-8 sm:px-10 cursor-grab active:cursor-grabbing select-none",
          isFullscreen && "pointer-events-auto",
        )}
      >
        <div className={cn("flex w-max gap-1.5", isFullscreen && "mx-auto")}>
          {images.map((img, index) => (
            <button
              key={`${variant}-thumb-${index}`}
              type="button"
              data-gallery-dot={index}
              onClick={(e) => {
                e.stopPropagation();
                handleThumbClick(index);
              }}
              aria-label={`${ariaLabelPrefix} ${index + 1}`}
              aria-current={index === currentIndex}
              className={cn(
                "relative shrink-0 overflow-hidden rounded-md border-2 transition-all touch-manipulation",
                isFullscreen
                  ? "h-12 w-16 sm:h-14 sm:w-[4.75rem]"
                  : "h-11 w-[3.75rem] sm:h-14 sm:w-[4.75rem]",
                index === currentIndex
                  ? isFullscreen
                    ? "border-white opacity-100"
                    : "border-primary ring-1 ring-primary/30 opacity-100"
                  : isFullscreen
                    ? "border-transparent opacity-55 hover:opacity-90"
                    : "border-transparent opacity-65 hover:opacity-100",
              )}
            >
              {isPaywall(img) ? (
                <div
                  className={cn(
                    "flex h-full w-full items-center justify-center",
                    isFullscreen ? "bg-white/10" : "bg-muted",
                  )}
                >
                  <Lock
                    className={cn(
                      "h-3.5 w-3.5",
                      isFullscreen ? "text-white/80" : "text-muted-foreground",
                    )}
                  />
                </div>
              ) : isVideoMedia(img.type, img.url) ? (
                <video
                  src={img.url}
                  muted
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-cover pointer-events-none"
                />
              ) : (
                <img
                  src={img.url}
                  alt=""
                  className="h-full w-full object-cover pointer-events-none"
                  loading="lazy"
                  draggable={false}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {canScrollPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            scrollByDir(-1);
          }}
          className={cn(
            "absolute left-1 top-1/2 z-10 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full border shadow-sm transition-colors",
            isFullscreen
              ? "pointer-events-auto border-white/30 bg-black/60 text-white hover:bg-black/80"
              : "border-border bg-background text-foreground hover:bg-muted",
          )}
          aria-label="Scroll thumbnails left"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      )}
      {canScrollNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            scrollByDir(1);
          }}
          className={cn(
            "absolute right-1 top-1/2 z-10 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full border shadow-sm transition-colors",
            isFullscreen
              ? "pointer-events-auto border-white/30 bg-black/60 text-white hover:bg-black/80"
              : "border-border bg-background text-foreground hover:bg-muted",
          )}
          aria-label="Scroll thumbnails right"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

interface CarImageGalleryProps {
  images: VehicleTestImages | undefined;
  isFavorite: boolean;
  listingId: string;
  onFavoriteChange?: (isFavorite: boolean) => void;
  isGuestMode?: boolean;
  guestHasMoreImages?: boolean;
  onGuestLoginRequired?: () => void;
  /** Full-bleed (no rounded frame) — used in För dig overlay sheet. */
  edgeToEdge?: boolean;
}

const CarImageGallery = ({
  images,
  isFavorite,
  listingId,
  onFavoriteChange,
  isGuestMode = false,
  guestHasMoreImages = false,
  onGuestLoginRequired,
  edgeToEdge = false,
}: CarImageGalleryProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const { language } = useLanguage();
  const labels = carDetailsLabels(language).gallery;
  const isMobile = useIsMobile();
  const { getDownloadUrlAsync } = useMedia();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  /** Engine video is outside the gallery scroll — shown only via the motor button. */
  const [engineMode, setEngineMode] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [imageHeight, setImageHeight] = useState<string>("50vh");
  const [loadingImages, setLoadingImages] = useState<Set<number>>(new Set());
  const [fullscreenImageUrls, setFullscreenImageUrls] = useState<
    Map<number, string>
  >(new Map());
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const mouseMoved = useRef(false);
  const nextImageRef = useRef<(() => Promise<void>) | null>(null);
  const prevImageRef = useRef<(() => Promise<void>) | null>(null);
  const lastSyncedIndex = useRef(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [fullscreenCarouselApi, setFullscreenCarouselApi] =
    useState<CarouselApi | null>(null);
  const lastFullscreenSyncedIndex = useRef(0);
  const isCarouselSyncingRef = useRef(false);
  const clearedCompleteImagesRef = useRef<Set<number>>(new Set());
  const galleryDotsScrollRef = useRef<HTMLDivElement>(null);
  const fullscreenDotsScrollRef = useRef<HTMLDivElement>(null);

  // Flatten VehicleTestImages to a single array with category
  const flattenImages = (images: VehicleTestImages | undefined) => {
    if (!images) return [];

    const flattenedImages: Array<ExteriorImage & { category: string }> = [];

    // Exterior images – exclude 360 frames (frameIndex/frame_index); those show only in 360 viewer
    if (images.exterior?.length) {
      images.exterior
        .filter((image) => !is360ExteriorFrame(image))
        .forEach((image) => {
          if (image.url) {
            flattenedImages.push({
              ...image,
              category: "exterior",
            });
          }
        });
    }

    // Interior images - preserve all metadata (id, position, name, type, size, url)
    if (images.interior?.length) {
      images.interior.forEach((image) => {
        if (image.url) {
          flattenedImages.push({
            ...image,
            category: "interior",
          });
        }
      });
    }

    return flattenedImages;
  };

  const { photoImages, engineVideo } = useMemo(() => {
    const flattenedImages = flattenImages(images);
    const filteredImages = flattenedImages.filter(
      (img) => img.url && img.url.trim() !== "",
    );
    const sorted = [...filteredImages].sort((a, b) => {
      const aIsThumbnail = "isThumbnail" in a && a.isThumbnail === true ? 1 : 0;
      const bIsThumbnail = "isThumbnail" in b && b.isThumbnail === true ? 1 : 0;
      return bIsThumbnail - aIsThumbnail;
    });
    const engine =
      sorted.find((img) => isEngineVideoSlide(img)) ?? null;
    const photos = sorted.filter((img) => !isEngineVideoSlide(img));
    return { photoImages: photos as GalleryPhoto[], engineVideo: engine as GalleryPhoto | null };
  }, [images]);

  const guestHasMoreImagesComputed = useMemo(() => {
    if (!isGuestMode) return false;
    return guestHasMoreImages || photoImages.length > 2;
  }, [isGuestMode, guestHasMoreImages, photoImages.length]);

  const visibleImages = useMemo(() => {
    if (!isGuestMode) return photoImages;
    // Guests can only preview the first 2 images across exterior+interior.
    return photoImages.slice(0, 2);
  }, [isGuestMode, photoImages]);

  const validImages = useMemo((): GallerySlide[] => {
    if (isGuestMode && guestHasMoreImagesComputed) {
      return [
        ...visibleImages,
        { category: "exterior", isGuestPaywall: true, url: "" },
      ];
    }
    return visibleImages;
  }, [isGuestMode, guestHasMoreImagesComputed, visibleImages]);

  const isPaywallSlide = (img: GallerySlide): boolean =>
    "isGuestPaywall" in img && img.isGuestPaywall === true;

  /** Blur preview uses last visible image — no extra URL from API. */
  const paywallBlurSourceUrl =
    visibleImages.length > 0
      ? visibleImages[visibleImages.length - 1]?.url
      : undefined;

  const renderPaywallSlide = () => (
    <div className="relative w-full h-full min-h-[200px] overflow-hidden bg-neutral-900 pointer-events-none">
      {paywallBlurSourceUrl ? (
        <>
          <NativeImage
            src={paywallBlurSourceUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl brightness-75 saturate-50"
            placeholder="none"
            aria-hidden
            draggable={false}
          />
          <NativeImage
            src={paywallBlurSourceUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover scale-105 blur-md opacity-60"
            placeholder="none"
            aria-hidden
            draggable={false}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-neutral-800" aria-hidden />
      )}
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" />
      <div className="relative z-10 flex h-full min-h-[inherit] flex-col items-center justify-center gap-4 p-6 text-center pointer-events-auto">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/50 ring-2 ring-white/30">
          <Lock className="h-7 w-7 text-white" strokeWidth={2} />
        </div>
        <p className="text-white text-sm md:text-base font-medium max-w-xs drop-shadow-md">
          {labels.guestMoreImages}
        </p>
        <Button
          type="button"
          className="bg-white text-black hover:bg-white/90 font-semibold shadow-lg"
          onClick={(e) => {
            e.stopPropagation();
            onGuestLoginRequired?.();
          }}
        >
          {labels.guestLoginCta}
        </Button>
      </div>
    </div>
  );

  // Calculate dynamic height based on screen width
  useEffect(() => {
    const calculateHeight = () => {
      const width = window.innerWidth;

      if (width >= 768) {
        // Desktop
        setImageHeight("500px");
      } else if (width >= 640) {
        // Larger mobile
        setImageHeight("384px"); // h-96
      } else if (width >= 425) {
        // Normal mobile (425px - 639px)
        setImageHeight("45vh");
      } else if (width >= 375) {
        // Small mobile (375px - 424px)
        setImageHeight("42vh");
      } else {
        // Extra small mobile (< 375px, e.g., 320px)
        setImageHeight("38vh");
      }
    };

    calculateHeight();
    // Debounce resize for better CPU performance
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateHeight, 150);
    };
    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Reset image index when listing changes
  useEffect(() => {
    if (validImages.length === 0) return;
    setCurrentImageIndex(0);
    setEngineMode(false);
    setLoadingImages(new Set());
    clearedCompleteImagesRef.current.clear();
  }, [listingId, validImages.length]);

  const clearImageLoading = useCallback((index: number) => {
    setLoadingImages((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  }, []);

  const markImageLoading = useCallback((index: number) => {
    const slide = validImages[index];
    if (slide && isPaywallSlide(slide)) {
      clearImageLoading(index);
      return;
    }
    setLoadingImages((prev) => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, [validImages, clearImageLoading]);

  /** Cached images: clear spinner once per index (ref must not loop setState). */
  const bindGalleryImageRef = useCallback(
    (index: number) => (el: HTMLImageElement | null) => {
      if (!el?.complete || el.naturalWidth === 0) return;
      if (clearedCompleteImagesRef.current.has(index)) return;
      clearedCompleteImagesRef.current.add(index);
      clearImageLoading(index);
    },
    [clearImageLoading],
  );

  const goToImageIndex = useCallback(
    (newIndex: number) => {
      if (validImages.length === 0) return;
      const clamped =
        ((newIndex % validImages.length) + validImages.length) %
        validImages.length;
      setEngineMode(false);
      setCurrentImageIndex(clamped);
      lastSyncedIndex.current = clamped;
      lastFullscreenSyncedIndex.current = clamped;
      isCarouselSyncingRef.current = true;
      try {
        if (isMobile && carouselApi) {
          carouselApi.scrollTo(clamped);
        }
        if (showFullscreen && fullscreenCarouselApi) {
          fullscreenCarouselApi.scrollTo(clamped);
        }
      } finally {
        requestAnimationFrame(() => {
          isCarouselSyncingRef.current = false;
        });
      }
      setLoadingImages((prev) => {
        if (!prev.has(clamped)) return prev;
        const next = new Set(prev);
        next.delete(clamped);
        return next;
      });
    },
    [validImages.length, isMobile, carouselApi, showFullscreen, fullscreenCarouselApi],
  );

  const preloadFullscreenImage = useCallback(
    async (index: number) => {
      const imageData = validImages[index];
      if (
        !imageData ||
        isPaywallSlide(imageData) ||
        !("fullSizeMediaId" in imageData) ||
        !imageData.fullSizeMediaId ||
        fullscreenImageUrls.has(index) ||
        !showFullscreen
      ) {
        return;
      }
      try {
        const downloadUrl = await getDownloadUrlAsync({
          mediaId: imageData.fullSizeMediaId,
          preferFullSize: true,
        });
        setFullscreenImageUrls((prev) => new Map(prev).set(index, downloadUrl));
      } catch {
        // ignore
      }
    },
    [validImages, fullscreenImageUrls, showFullscreen, getDownloadUrlAsync],
  );

  const nextImage = useCallback(async () => {
    if (validImages.length === 0) return;
    const newIndex = (currentImageIndex + 1) % validImages.length;
    goToImageIndex(newIndex);
    await preloadFullscreenImage(newIndex);
  }, [validImages.length, currentImageIndex, goToImageIndex, preloadFullscreenImage]);

  const prevImage = useCallback(async () => {
    if (validImages.length === 0) return;
    const newIndex =
      (currentImageIndex - 1 + validImages.length) % validImages.length;
    goToImageIndex(newIndex);
    await preloadFullscreenImage(newIndex);
  }, [validImages.length, currentImageIndex, goToImageIndex, preloadFullscreenImage]);

  // Update refs when functions change
  useEffect(() => {
    nextImageRef.current = nextImage;
    prevImageRef.current = prevImage;
  }, [nextImage, prevImage]);

  // Sync Embla carousel selected index with state (mobile gallery)
  // Clear loading for selected index – carousel images are already in DOM and loaded, so no spinner after swipe
  useEffect(() => {
    if (!carouselApi || !isMobile) return;
    const onSelect = () => {
      if (isCarouselSyncingRef.current) return;
      const idx = carouselApi.selectedScrollSnap();
      if (idx === lastSyncedIndex.current) return;
      lastSyncedIndex.current = idx;
      setCurrentImageIndex(idx);
      setLoadingImages((prev) => {
        if (!prev.has(idx)) return prev;
        const next = new Set(prev);
        next.delete(idx);
        return next;
      });
    };
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi, isMobile]);

  // When currentImageIndex is set externally (e.g. from fullscreen or arrows), scroll carousel on mobile
  useEffect(() => {
    if (
      !isMobile ||
      !carouselApi ||
      currentImageIndex === lastSyncedIndex.current
    )
      return;
    if (carouselApi.selectedScrollSnap() === currentImageIndex) return;
    isCarouselSyncingRef.current = true;
    carouselApi.scrollTo(currentImageIndex);
    lastSyncedIndex.current = currentImageIndex;
    requestAnimationFrame(() => {
      isCarouselSyncingRef.current = false;
    });
  }, [isMobile, carouselApi, currentImageIndex]);

  // Fullscreen Embla: sync selected index with state
  useEffect(() => {
    if (!fullscreenCarouselApi || !showFullscreen) return;
    const onSelect = () => {
      if (isCarouselSyncingRef.current) return;
      const idx = fullscreenCarouselApi.selectedScrollSnap();
      if (idx === lastFullscreenSyncedIndex.current) return;
      lastFullscreenSyncedIndex.current = idx;
      setCurrentImageIndex(idx);
    };
    fullscreenCarouselApi.on("select", onSelect);
    return () => {
      fullscreenCarouselApi.off("select", onSelect);
    };
  }, [fullscreenCarouselApi, showFullscreen]);

  // Fullscreen: when currentImageIndex changes (dots/arrows), scroll carousel; when dialog opens, scroll to current
  useEffect(() => {
    if (!showFullscreen || !fullscreenCarouselApi) return;
    if (fullscreenCarouselApi.selectedScrollSnap() === currentImageIndex) return;
    isCarouselSyncingRef.current = true;
    fullscreenCarouselApi.scrollTo(currentImageIndex);
    lastFullscreenSyncedIndex.current = currentImageIndex;
    requestAnimationFrame(() => {
      isCarouselSyncingRef.current = false;
    });
  }, [showFullscreen, fullscreenCarouselApi, currentImageIndex]);

  // Thumbnail click on mobile: scroll carousel to that index
  const handleThumbnailClick = useCallback(
    (index: number) => {
      goToImageIndex(index);
    },
    [goToImageIndex],
  );

  const { isNative } = useNativePlatform();
  const openFullscreen = useCallback(() => {
    setGalleryFullscreenOpen(true);
    setShowFullscreen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setGalleryFullscreenOpen(false);
    setShowFullscreen(false);
  }, []);

  // Keep sync lock aligned if fullscreen is closed by any path
  useEffect(() => {
    setGalleryFullscreenOpen(showFullscreen);
    return () => {
      if (showFullscreen) setGalleryFullscreenOpen(false);
    };
  }, [showFullscreen]);
  const imageGestureLockRef = useMediaViewerGestureLock();
  const { imageZoomed, imageZoomedRef, onZoomedChange, resetZoomed } =
    useMediaViewerZoomState();
  const fullscreenCarouselOpts = useMemo(
    () => ({
      align: "center" as const,
      loop: false,
      dragFree: false,
      watchDrag: () => !imageZoomedRef.current,
    }),
    [imageZoomedRef],
  );

  useEffect(() => {
    if (!showFullscreen) resetZoomed();
  }, [showFullscreen, resetZoomed]);

  useEffect(() => {
    if (!fullscreenCarouselApi || !showFullscreen) return;
    fullscreenCarouselApi.reInit();
  }, [fullscreenCarouselApi, showFullscreen, imageZoomed]);

  useNativeMediaViewerOpen(showFullscreen);

  // Escape closes fullscreen on all devices — window capture beats Radix document capture
  useEffect(() => {
    if (!showFullscreen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      closeFullscreen();
    };
    window.addEventListener("keydown", handleEscape, true);
    return () => window.removeEventListener("keydown", handleEscape, true);
  }, [showFullscreen, closeFullscreen]);

  // Arrow navigation for desktop fullscreen
  useEffect(() => {
    if (!showFullscreen || isMobile) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (imageZoomedRef.current) return;
      if (
        e.key === "ArrowLeft" &&
        validImages.length > 1 &&
        prevImageRef.current
      ) {
        e.preventDefault();
        prevImageRef.current();
      } else if (
        e.key === "ArrowRight" &&
        validImages.length > 1 &&
        nextImageRef.current
      ) {
        e.preventDefault();
        nextImageRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [
    showFullscreen,
    isMobile,
    validImages.length,
    nextImage,
    prevImage,
    imageZoomedRef,
  ]);

  useEffect(() => {
    const scrollActiveDot = (container: HTMLDivElement | null) => {
      if (!container) return;
      const active = container.querySelector<HTMLElement>(
        `[data-gallery-dot="${currentImageIndex}"]`,
      );
      active?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    };

    scrollActiveDot(galleryDotsScrollRef.current);
    if (showFullscreen) {
      scrollActiveDot(fullscreenDotsScrollRef.current);
    }
  }, [currentImageIndex, showFullscreen]);

  const swipeDismiss = useFullscreenSwipeDismiss(closeFullscreen, {
    gestureLockRef: imageGestureLockRef,
  });
  const fullscreenSwipeHandlers = useMemo(
    () => ({
      onTouchStart: swipeDismiss.onTouchStart,
      onTouchMove: swipeDismiss.onTouchMove,
      onTouchEnd: swipeDismiss.onTouchEnd,
    }),
    [
      swipeDismiss.onTouchStart,
      swipeDismiss.onTouchMove,
      swipeDismiss.onTouchEnd,
    ],
  );

  // If no valid images, show placeholder
  if (validImages.length === 0) {
    return (
      <div
        className="w-full rounded-none sm:rounded-lg bg-gray-200 flex items-center justify-center"
        style={{ height: imageHeight }}
      >
        <p className="text-gray-500">{labels.noImages}</p>
      </div>
    );
  }

  const currentImage = validImages[currentImageIndex];
  const isCurrentPaywall =
    !!currentImage && isPaywallSlide(currentImage);
  const showGalleryLoader =
    !isNative &&
    loadingImages.has(currentImageIndex) &&
    !isCurrentPaywall;

  const handleImageLoad = (index: number) => {
    clearImageLoading(index);
  };

  const handleImageLoadStart = (index: number) => {
    markImageLoading(index);
  };

  // Map category to localized text
  const getCategoryText = (category: string) => {
    switch (category) {
      case "exterior":
        return labels.exterior;
      case "interior":
        return labels.interior;
      default:
        return labels.unknown;
    }
  };

  const handleFavoriteClick = () => {
    // Just call the callback - parent component handles the actual favorite toggle
    onFavoriteChange?.(!isFavorite);
  };

  const handleShareClick = async () => {
    // Native WebView origin is localhost — share the public web URL instead
    const shareUrl = getShareableAppUrl();
    const shareTitle = labels.carForSale;
    const shareText = labels.shareText;

    try {
      // Capacitor Share is required on iOS native — navigator.share is unreliable there
      if (isNative) {
        const { Share } = await import("@capacitor/share");
        const canShare = await Share.canShare();
        if (canShare.value) {
          await Share.share({
            title: shareTitle,
            text: shareText,
            url: shareUrl,
            dialogTitle: shareTitle,
          });
          return;
        }
        await navigator.clipboard.writeText(shareUrl);
        toast.success(labels.linkCopied, {
          description: labels.linkCopiedDesc,
        });
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(labels.linkCopied, {
          description: labels.linkCopiedDesc,
        });
      }
    } catch (error) {
      // User cancelled share sheet — ignore
      if (
        error instanceof Error &&
        (error.name === "AbortError" || error.message?.includes("cancel"))
      ) {
        return;
      }
      console.error("Error sharing:", error);
    }
  };

  const handleImageClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const clicked = validImages[currentImageIndex];
    if (clicked && isPaywallSlide(clicked)) {
      onGuestLoginRequired?.();
      return;
    }

    if (
      clicked &&
      "fullSizeMediaId" in clicked &&
      clicked.fullSizeMediaId &&
      !fullscreenImageUrls.has(currentImageIndex)
    ) {
      try {
        const downloadUrl = await getDownloadUrlAsync({
          mediaId: clicked.fullSizeMediaId,
          preferFullSize: true,
        });
        setFullscreenImageUrls((prev) =>
          new Map(prev).set(currentImageIndex, downloadUrl),
        );
      } catch (error) {
        // Continue with existing URL if fullSize fetch fails
      }
    }

    openFullscreen();
  };

  // Swipe handlers for touch devices
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (imageZoomedRef.current) return;
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && validImages.length > 1) {
      nextImage();
    }
    if (isRightSwipe && validImages.length > 1) {
      prevImage();
    }
  };

  // Mouse drag handlers for desktop
  const onMouseDown = (e: React.MouseEvent) => {
    mouseMoved.current = false;
    touchEndX.current = null;
    touchStartX.current = e.clientX;
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (touchStartX.current !== null) {
      const moved = Math.abs(e.clientX - (touchStartX.current || 0)) > 5;
      if (moved) {
        mouseMoved.current = true;
      }
      touchEndX.current = e.clientX;
    }
  };

  const onMouseUp = () => {
    if (imageZoomedRef.current) {
      touchStartX.current = null;
      touchEndX.current = null;
      mouseMoved.current = false;
      return;
    }
    // Only trigger swipe if mouse was actually moved (not just a click)
    if (!mouseMoved.current) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }

    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && validImages.length > 1) {
      nextImage();
    }
    if (isRightSwipe && validImages.length > 1) {
      prevImage();
    }
    touchStartX.current = null;
    touchEndX.current = null;
    mouseMoved.current = false;
  };

  return (
    <>
      <div className={cn("w-full", edgeToEdge && "bg-black")}>
      <div
        ref={galleryRef}
        className={cn(
          "relative w-full overflow-hidden bg-black",
          edgeToEdge
            ? "rounded-none"
            : "rounded-none sm:rounded-lg",
          isNative && "native-listing-gallery-bleed",
          !edgeToEdge && validImages.length > 1 && "sm:rounded-b-none",
          // Bleed past any residual sheet padding/hairline so white never peeks
          edgeToEdge && "-mt-px -ml-px w-[calc(100%+1px)] pt-px",
        )}
        style={{ height: edgeToEdge ? (isMobile ? "42vh" : "46vh") : imageHeight }}
        onTouchStart={
          isMobile && validImages.length > 1 ? undefined : onTouchStart
        }
        onTouchMove={
          isMobile && validImages.length > 1 ? undefined : onTouchMove
        }
        onTouchEnd={isMobile && validImages.length > 1 ? undefined : onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {showGalleryLoader && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100/90 dark:bg-gray-800/90 z-10 pointer-events-none">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {engineMode && engineVideo?.url ? (
          <div className="w-full h-full flex items-center justify-center bg-black relative">
            <GalleryVideoPlayer
              src={engineVideo.url}
              onReady={() => clearImageLoading(currentImageIndex)}
            />
          </div>
        ) : isMobile && validImages.length > 1 ? (
          <Carousel
            setApi={setCarouselApi}
            opts={{ align: "start", loop: false, dragFree: false }}
            className="h-full w-full [&_.overflow-hidden]:!h-full"
          >
            <CarouselContent className="h-full ml-0 pl-0">
              {validImages.map((img, index) => (
                <CarouselItem
                  key={`carousel-${index}`}
                  className="pl-0 basis-full flex-shrink-0 flex-grow-0"
                >
                  <div
                    className="relative w-full h-full flex items-center justify-center bg-black"
                    onClick={
                      isPaywallSlide(img) ? () => onGuestLoginRequired?.() : handleImageClick
                    }
                  >
                    {isPaywallSlide(img) ? (
                      renderPaywallSlide()
                    ) : isVideoMedia(img.type, img.url) ? (
                      <GalleryVideoPlayer
                        src={img.url}
                        onReady={() => handleImageLoad(index)}
                      />
                    ) : (
                      <NativeImage
                        ref={bindGalleryImageRef(index)}
                        src={img.url}
                        alt={`${labels.carForSale} ${index + 1}`}
                        className="max-w-full max-h-full w-auto h-auto object-contain select-none touch-none"
                        placeholder="none"
                        draggable={false}
                        style={{ imageRendering: "-webkit-optimize-contrast" }}
                        loading="eager"
                        onLoadStart={() => handleImageLoadStart(index)}
                        onLoad={() => handleImageLoad(index)}
                        onError={() => handleImageLoad(index)}
                      />
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        ) : (
          /* Desktop or single image: single-image view */
          <div className="w-full h-full flex items-center justify-center bg-black relative">
            {isPaywallSlide(currentImage) ? (
              renderPaywallSlide()
            ) : isVideoMedia(currentImage.type, currentImage.url) ? (
              <GalleryVideoPlayer
                src={currentImage.url}
                onReady={() => handleImageLoad(currentImageIndex)}
              />
            ) : (
              <NativeImage
                ref={bindGalleryImageRef(currentImageIndex)}
                src={currentImage.url}
                alt={`${labels.carForSale} ${currentImageIndex + 1}`}
                className="max-w-full max-h-full w-auto h-auto object-contain cursor-pointer select-none"
                placeholder="none"
                onClick={handleImageClick}
                draggable={false}
                style={{
                  imageRendering: "-webkit-optimize-contrast",
                }}
                loading="eager"
                onLoadStart={() => handleImageLoadStart(currentImageIndex)}
                onLoad={() => handleImageLoad(currentImageIndex)}
                onError={() => handleImageLoad(currentImageIndex)}
              />
            )}
          </div>
        )}

        {/* Category + counter — bottom left (hidden while playing engine video badge is enough) */}
        <div className="absolute bottom-4 left-3 z-10 flex max-w-[30%] flex-col items-start gap-1 pointer-events-none">
          <span className="inline-block px-2 py-0.5 rounded-md bg-black/55 text-white text-xs font-medium leading-tight">
            {engineMode
              ? (labels.engineVideo ?? "Motorvideo")
              : currentImage && isPaywallSlide(currentImage)
                ? labels.guestPaywallCategory
                : getCategoryText(currentImage?.category ?? "exterior")}
          </span>
          {!engineMode && validImages.length > 1 && (
            <span
              className={`inline-block px-2 py-0.5 rounded-md text-xs font-semibold leading-tight ${
                isDark ? "bg-black/55 text-white" : "bg-white/90 text-gray-800"
              }`}
            >
              {currentImageIndex + 1} / {validImages.length}
            </span>
          )}
        </div>

        {/* Share + favorite — top right (extra top inset on native under status bar) */}
        <div
          className={cn(
            "absolute right-3 z-10 flex items-center gap-1.5",
            isNative
              ? "top-[calc(var(--native-safe-top,env(safe-area-inset-top,0px))+8px)]"
              : "top-3",
          )}
        >
          <button
            type="button"
            onClick={handleShareClick}
            className="group flex h-11 w-11 items-center justify-center rounded-full p-0 text-sky-400 hover:bg-black/20 active:bg-black/30 touch-manipulation transition-transform duration-200 hover:scale-105 active:scale-95"
            aria-label="Dela"
          >
            <Share2 className="h-8 w-8 shrink-0 drop-shadow-md transition-all duration-200 group-hover:fill-sky-400/25 group-hover:scale-105" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={handleFavoriteClick}
            className="group flex h-11 w-11 items-center justify-center rounded-full p-0 hover:bg-black/20 active:bg-black/30 touch-manipulation transition-transform duration-200 hover:scale-105 active:scale-95"
            aria-label="Favorit"
          >
            <Heart
              className={`h-8 w-8 shrink-0 drop-shadow-md transition-all duration-200 group-hover:scale-105 ${
                isFavorite ? "fill-red-500 text-red-500" : "text-red-400 group-hover:fill-red-500/40"
              }`}
              fill={isFavorite ? "currentColor" : "none"}
              strokeWidth={2}
            />
          </button>
        </div>

        {/* Navigation arrows */}
        {!engineMode && validImages.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                void prevImage();
              }}
              className={`absolute left-4 top-1/2 transform -translate-y-1/2 z-20 p-2 rounded-full ${
                isDark
                  ? "bg-black/50 hover:bg-black/70"
                  : "bg-white/90 hover:bg-white"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                void nextImage();
              }}
              className={`absolute right-4 top-1/2 transform -translate-y-1/2 z-20 p-2 rounded-full ${
                isDark
                  ? "bg-black/50 hover:bg-black/70"
                  : "bg-white/90 hover:bg-white"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Button>
          </>
        )}

      </div>

      {(validImages.length > 1 || engineVideo) && (
        <div
          className={cn(
            "flex items-center gap-1.5 bg-background border-t border-border/50 rounded-b-none sm:rounded-b-lg py-2 pr-2",
            edgeToEdge && "overflow-hidden rounded-none border-x-0",
          )}
        >
          {validImages.length > 1 ? (
            <div className="min-w-0 flex-1">
              <GalleryThumbStrip
                images={validImages}
                currentIndex={engineMode ? -1 : currentImageIndex}
                scrollRef={galleryDotsScrollRef}
                onSelect={handleThumbnailClick}
                isPaywall={isPaywallSlide}
                ariaLabelPrefix={labels.carForSale}
                variant="default"
                embedded
              />
            </div>
          ) : (
            <div className="flex-1" />
          )}
          {engineVideo?.url ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEngineMode(true);
              }}
              className={cn(
                "relative shrink-0 overflow-hidden rounded-2xl transition-all touch-manipulation",
                "h-11 w-[3.75rem] sm:h-14 sm:w-[4.75rem]",
                "border border-white/45 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]",
                engineMode
                  ? "border-white/80 opacity-100"
                  : "opacity-95 hover:opacity-100 hover:border-white/65",
              )}
              aria-label={labels.engineVideo ?? "Motorvideo"}
              title={labels.engineVideo ?? "Motorvideo"}
              aria-pressed={engineMode}
            >
              <EngineVideoThumb
                src={engineVideo.url}
                className="absolute inset-0 h-full w-full object-cover pointer-events-none bg-black/20"
              />
              <span className="absolute inset-0 bg-black/45" />
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white">
                <Play
                  className="h-4 w-4 fill-white drop-shadow-md translate-x-px"
                  strokeWidth={0}
                />
                <span className="text-[9px] font-medium tracking-[0.14em] uppercase text-white/95 drop-shadow-sm">
                  {labels.engineVideoShort ?? "Motor"}
                </span>
              </span>
            </button>
          ) : null}
        </div>
      )}
      </div>

      {/* Fullscreen overlay – portal to body so shared Dialog styles don't affect it (fixes mobile) */}
      {showFullscreen &&
        createPortal(
          <div
            data-bilenia-gallery-fullscreen=""
            className={cn(
              "native-media-viewer-layer fixed inset-0 h-dvh w-dvh bg-black",
              // Above För dig sheet (211) / native sheets (201)
              mediaViewerZClass(),
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Fullscreen image"
          >
            <div
              ref={fullscreenRef}
              className="relative w-full h-full flex items-center justify-center overflow-hidden bg-black"
              onTouchStart={(e) => {
                if (imageZoomedRef.current) return;
                fullscreenSwipeHandlers.onTouchStart?.(e);
                if (validImages.length <= 1) onTouchStart(e);
              }}
              onTouchMove={(e) => {
                if (imageZoomedRef.current) return;
                fullscreenSwipeHandlers.onTouchMove?.(e);
                if (validImages.length <= 1) onTouchMove(e);
              }}
              onTouchEnd={(e) => {
                if (imageZoomedRef.current) return;
                fullscreenSwipeHandlers.onTouchEnd?.(e);
                if (validImages.length <= 1) onTouchEnd();
              }}
              onMouseDown={
                validImages.length <= 1 && !imageZoomed
                  ? onMouseDown
                  : undefined
              }
              onMouseMove={
                validImages.length <= 1 && !imageZoomed
                  ? onMouseMove
                  : undefined
              }
              onMouseUp={
                validImages.length <= 1 && !imageZoomed ? onMouseUp : undefined
              }
              onMouseLeave={
                validImages.length <= 1 && !imageZoomed ? onMouseUp : undefined
              }
            >
              <button
                onClick={closeFullscreen}
                className={`absolute ${
                  isMobile ? "top-12 right-2" : "top-2 right-2"
                } z-[9999] bg-black/50 hover:bg-black/70 ${
                  isMobile ? "p-3" : "p-2"
                } rounded-full transition-colors`}
                aria-label="Close fullscreen"
              >
                <X
                  className={`${isMobile ? "h-7 w-7" : "h-6 w-6"} text-white`}
                />
              </button>

              {/* Loading indicator for fullscreen (single-image mode only) */}
              {validImages.length <= 1 &&
                loadingImages.has(currentImageIndex) && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 bg-black">
                    <Loader2 className="h-12 w-12 animate-spin text-white" />
                  </div>
                )}

              {validImages.length > 1 ? (
                /* Fullscreen Embla carousel – swipe/drag follows finger */
                <Carousel
                  setApi={setFullscreenCarouselApi}
                  opts={fullscreenCarouselOpts}
                  className="absolute inset-0 w-full h-full [&_.overflow-hidden]:!h-full"
                >
                  <CarouselContent className="h-full ml-0 pl-0">
                    {validImages.map((img, index) => (
                      <CarouselItem
                        key={`fs-${index}`}
                        className="pl-0 basis-full flex-shrink-0 flex-grow-0"
                      >
                        <div className="flex justify-center items-center w-full h-full p-4">
                          {isPaywallSlide(img) ? (
                            <div className="w-full max-w-[calc(100vw-4rem)] max-h-[calc(100dvh-4rem)] min-h-[280px]">
                              {renderPaywallSlide()}
                            </div>
                          ) : isVideoMedia(
                              img.type,
                              fullscreenImageUrls.get(index) || img.url,
                            ) ? (
                            <video
                              src={fullscreenImageUrls.get(index) || img.url}
                              controls
                              playsInline
                              preload="metadata"
                              className="max-w-full max-h-full rounded shadow-lg"
                              onLoadedData={() => handleImageLoad(index)}
                              onError={() => handleImageLoad(index)}
                            />
                          ) : (
                            <ZoomableImage
                              src={fullscreenImageUrls.get(index) || img.url}
                              alt={`Bild fullscreen ${index + 1}`}
                              className="h-full w-full"
                              imgClassName="rounded shadow-lg"
                              draggable={false}
                              gestureLockRef={imageGestureLockRef}
                              onZoomedChange={onZoomedChange}
                              onLoad={() => handleImageLoad(index)}
                              onError={() => handleImageLoad(index)}
                            />
                          )}
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              ) : (
                <div className="flex justify-center items-center w-full h-full p-4">
                  {isVideoMedia(
                    currentImage.type,
                    fullscreenImageUrls.get(currentImageIndex) ||
                      currentImage.url,
                  ) ? (
                    <video
                      src={
                        fullscreenImageUrls.get(currentImageIndex) ||
                        currentImage.url
                      }
                      controls
                      playsInline
                      preload="metadata"
                      className="max-w-full max-h-full rounded shadow-lg"
                      onLoadedData={() => handleImageLoad(currentImageIndex)}
                      onError={() => handleImageLoad(currentImageIndex)}
                    />
                  ) : (
                    <ZoomableImage
                      src={
                        fullscreenImageUrls.get(currentImageIndex) ||
                        currentImage.url
                      }
                      alt={`Bild fullscreen ${currentImageIndex + 1}`}
                      className={cn(
                        "h-full w-full transition-opacity duration-200",
                        loadingImages.has(currentImageIndex)
                          ? "opacity-0"
                          : "opacity-100",
                      )}
                      imgClassName="rounded shadow-lg"
                      draggable={false}
                      gestureLockRef={imageGestureLockRef}
                      onZoomedChange={onZoomedChange}
                      onLoad={() => handleImageLoad(currentImageIndex)}
                      onError={() => handleImageLoad(currentImageIndex)}
                    />
                  )}
                </div>
              )}

              {validImages.length > 1 && !imageZoomed && (
                <>
                  <button
                    onClick={prevImage}
                    className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 ${
                      isMobile ? "p-3" : "p-2"
                    } rounded-full transition-colors`}
                    aria-label="Previous image"
                  >
                    <ChevronLeft
                      className={`${isMobile ? "h-8 w-8" : "h-6 w-6"} text-white`}
                    />
                  </button>
                  <button
                    onClick={nextImage}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 ${
                      isMobile ? "p-3" : "p-2"
                    } rounded-full transition-colors`}
                    aria-label="Next image"
                  >
                    <ChevronRight
                      className={`${isMobile ? "h-8 w-8" : "h-6 w-6"} text-white`}
                    />
                  </button>

                  {/* Thumbnail strip in fullscreen */}
                  <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/85 via-black/40 to-transparent pt-8 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]">
                    <GalleryThumbStrip
                      images={validImages}
                      currentIndex={currentImageIndex}
                      scrollRef={fullscreenDotsScrollRef}
                      onSelect={goToImageIndex}
                      isPaywall={isPaywallSlide}
                      ariaLabelPrefix={labels.carForSale}
                      variant="fullscreen"
                    />
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default CarImageGallery;
